import { BasketStats } from '@domain'
import dayjs from 'dayjs'

export function toDailySavingsDataset(data: BasketStats[]) {
	if (data.length === 0) return []

	// Group market baskets by date and add up savings
	const groupedByDay = data.reduce((acc, stats) => {
		const day = dayjs(stats.createdAt).format('YYYY-MM-DD')
		if (!acc[day]) {
			acc[day] = { ...stats }
		} else {
			acc[day].totalAnnualSavings += stats.totalAnnualSavings || 0
			acc[day].annualRebateSavings += stats.annualRebateSavings || 0
			acc[day].annualPriceSavings += stats.annualPriceSavings || 0
		}
		return acc
	}, {})

	// Ensure proper sorting
	const sortedDays = Object.keys(groupedByDay).sort((a, b) =>
		dayjs(a).isBefore(dayjs(b)) ? -1 : 1
	)

	return sortedDays.map((day) => {
		const stat = groupedByDay[day]

		return {
			date: dayjs(day).toDate(),
			totalAnnualSavings: stat.totalAnnualSavings || 0,
			annualPriceSavings: stat.annualPriceSavings || 0,
			annualRebateSavings: stat.annualRebateSavings || 0,
		}
	})
}

export function buildDateRange(range: string) {
	const now = dayjs()
	let start: dayjs.Dayjs
	let end: dayjs.Dayjs

	switch (range) {
		case '7-days':
			start = now.subtract(6, 'day').startOf('day')
			end = now.endOf('day')
			break

		case '30-days':
			start = now.subtract(29, 'day').startOf('day')
			end = now.endOf('day')
			break

		case '6-months':
			start = now.subtract(6, 'month').startOf('day')
			end = now.endOf('day')
			break

		case 'year-to-date':
			start = now.startOf('year')
			end = now.endOf('day')
			break

		case 'quarter-1':
			start = now.startOf('year')
			end = now.startOf('year').add(3, 'month').subtract(1, 'millisecond')
			break

		case 'quarter-2':
			start = now.startOf('year').add(3, 'month')
			end = now.startOf('year').add(6, 'month').subtract(1, 'millisecond')
			break

		case 'quarter-3':
			start = now.startOf('year').add(6, 'month')
			end = now.startOf('year').add(9, 'month').subtract(1, 'millisecond')
			break

		case 'quarter-4':
			start = now.startOf('year').add(9, 'month')
			end = now.endOf('year')
			break

		default:
			// fallback
			start = now.subtract(6, 'day').startOf('day')
			end = now.endOf('day')
	}

	return {
		dateRangeStart: start.format('YYYY-MM-DD'),
		dateRangeEnd: end.format('YYYY-MM-DD'),
	}
}

export const dateRanges = [
	{ label: 'Past 7 Days', value: '7-days' },
	{ label: 'Past 30 Days', value: '30-days' },
	{ label: 'Past 6 Months', value: '6-months' },
	{ label: 'Year to Date', value: 'year-to-date' },
	{ label: 'Quarter 1', value: 'quarter-1' },
	{ label: 'Quarter 2', value: 'quarter-2' },
	{ label: 'Quarter 3', value: 'quarter-3' },
	{ label: 'Quarter 4', value: 'quarter-4' },
]

function toSavingsPcts(d: BasketStats) {
	const spend = d.totalSpend || 0
	if (!spend) return { totalPct: 0, pricePct: 0, rebatePct: 0 }

	return {
		totalPct: d.totalAnnualSavings / spend,
		pricePct: d.annualPriceSavings / spend,
		rebatePct: d.annualRebateSavings / spend,
	}
}

function groupByDay(data: BasketStats[]) {
	const groups = new Map<string, BasketStats[]>()

	data.forEach((d) => {
		const day = dayjs(d.createdAt).format('YYYY-MM-DD')
		const existing = groups.get(day) ?? []
		existing.push(d)
		groups.set(day, existing)
	})

	return groups
}

function groupByWeek(data: BasketStats[]) {
	const groups = new Map<string, BasketStats[]>()

	data.forEach((d) => {
		const weekStart = dayjs(d.createdAt).startOf('week').format('YYYY-MM-DD')
		const existing = groups.get(weekStart) ?? []
		existing.push(d)
		groups.set(weekStart, existing)
	})

	return groups
}

function average(values: number[]) {
	return values.reduce((sum, v) => sum + v, 0) / values.length
}

export function toAverageSavingsPcts(data: BasketStats[], interval: 'day' | 'week') {
	if (data.length === 0) return []

	const groups = interval === 'day' ? groupByDay(data) : groupByWeek(data)

	return (
		[...groups.entries()]
			// Sort chronologically
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([key, baskets]) => {
				// Convert each basket's savings to percentages of total spend
				const percentages = baskets.map(toSavingsPcts)

				// Average each percentage across all baskets for this period
				return {
					date: dayjs(key).toDate(),
					totalPct: average(percentages.map((p) => p.totalPct)),
					pricePct: average(percentages.map((p) => p.pricePct)),
					rebatePct: average(percentages.map((p) => p.rebatePct)),
				}
			})
	)
}
